import React, { useEffect, useState } from 'react';
import api from '../../lib/api';
import { Package, ShoppingBag, Users, Loader2, TrendingUp, DollarSign, Boxes, Clock, Truck, CheckCircle2, XCircle, AlertTriangle, PackageX, Plus } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from '../../hooks/use-toast';

const KpiCard = ({ icon: Icon, label, value, color, hint, big = false }) => (
  <div className={`bg-neutral-950 border border-white/10 rounded-2xl p-4 flex items-center gap-3 ${big ? 'sm:p-5' : ''}`}>
    <div className={`shrink-0 w-11 h-11 rounded-xl flex items-center justify-center ${color}`}>
      <Icon className="w-5 h-5" />
    </div>
    <div className="min-w-0">
      <p className="text-white/50 text-[10px] uppercase tracking-wider font-bold">{label}</p>
      <p className={`font-black text-white leading-tight ${big ? 'text-2xl md:text-3xl' : 'text-xl'}`}>{value}</p>
      {hint && <p className="text-white/40 text-[11px] mt-0.5 truncate">{hint}</p>}
    </div>
  </div>
);

const PIPELINE = [
  { key: 'Preparando', label: 'Preparando', Icon: Clock, color: 'bg-amber-500/15 text-amber-300 border-amber-500/30', dot: 'bg-amber-400' },
  { key: 'En camino', label: 'En camino', Icon: Truck, color: 'bg-blue-500/15 text-blue-300 border-blue-500/30', dot: 'bg-blue-400' },
  { key: 'Entregado', label: 'Entregado', Icon: CheckCircle2, color: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30', dot: 'bg-emerald-400' },
  { key: 'Cancelado', label: 'Cancelado', Icon: XCircle, color: 'bg-rose-500/15 text-rose-300 border-rose-500/30', dot: 'bg-rose-400' },
];

const OrderPipeline = ({ counts = {}, total = 0 }) => {
  return (
    <div className="bg-neutral-950 border border-white/10 rounded-2xl p-4 md:p-5">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <ShoppingBag className="w-4 h-4 text-orange-400" />
          <h3 className="text-white font-bold text-sm uppercase tracking-wider">Pipeline de pedidos</h3>
        </div>
        <span className="text-[11px] text-white/50 font-semibold">{total} totales</span>
      </div>

      {/* Compact horizontal pipeline with connectors */}
      <div className="grid grid-cols-4 gap-1.5 md:gap-2">
        {PIPELINE.map((s, i) => {
          const n = counts[s.key] || 0;
          const pct = total > 0 ? Math.round((n / total) * 100) : 0;
          return (
            <div key={s.key} className={`relative rounded-xl border px-2 py-2.5 md:px-3 md:py-3 ${s.color}`}>
              <div className="flex items-center gap-1.5">
                <s.Icon className="w-3.5 h-3.5" />
                <span className="text-[10px] uppercase tracking-wider font-black truncate">{s.label}</span>
              </div>
              <div className="flex items-baseline gap-1 mt-1">
                <span className="text-2xl md:text-3xl font-black text-white leading-none">{n}</span>
                <span className="text-[10px] text-white/60 font-semibold">/{total || 0}</span>
              </div>
              <div className="mt-2 h-1 rounded-full bg-black/40 overflow-hidden">
                <div className={`h-full ${s.dot}`} style={{ width: `${pct}%` }} />
              </div>
              {/* Connector arrow (except last) */}
              {i < PIPELINE.length - 1 && (
                <div className="hidden md:block absolute -right-1.5 top-1/2 -translate-y-1/2 w-3 h-3 text-white/20">
                  <svg viewBox="0 0 8 8" fill="currentColor"><polygon points="0,0 6,4 0,8" /></svg>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const LowStockPanel = ({ items = [], threshold = 3, step = 10, onChanged }) => {
  const [busy, setBusy] = React.useState(null);

  const restock = async (p, delta) => {
    setBusy(p.product_id);
    try {
      const r = await api.post(`/admin/products/${p.product_id}/stock/adjust`, { delta });
      toast({ title: 'Stock actualizado', description: `${p.title}: +${delta} → ${r.data.stock} u.` });
      if (onChanged) onChanged();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'No se pudo actualizar' });
    } finally { setBusy(null); }
  };

  if (!items.length) {
    return (
      <div className="mt-5 rounded-2xl border border-emerald-500/20 bg-emerald-500/5 p-4 flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-emerald-500/15 text-emerald-300 flex items-center justify-center">
          <CheckCircle2 className="w-5 h-5" />
        </div>
        <div>
          <p className="text-white font-bold text-sm">Todo el inventario está saludable</p>
          <p className="text-white/50 text-xs">No hay productos con stock crítico (≤ 3 unidades).</p>
        </div>
      </div>
    );
  }

  const outOfStock = items.filter((p) => (p.stock || 0) === 0).length;

  return (
    <div className="mt-5 rounded-2xl border border-amber-500/25 bg-gradient-to-br from-amber-500/5 via-neutral-950 to-neutral-950 p-4 md:p-5">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-xl bg-amber-500/15 text-amber-300 flex items-center justify-center border border-amber-500/30">
            <AlertTriangle className="w-4 h-4" />
          </div>
          <div>
            <h3 className="text-white font-bold text-sm uppercase tracking-wider flex items-center gap-2">
              Stock bajo o agotado
              <span className="text-[10px] font-black text-amber-300 bg-amber-500/15 border border-amber-500/30 px-2 py-0.5 rounded-full">
                {items.length}
              </span>
            </h3>
            <p className="text-white/50 text-[11px] mt-0.5">
              Productos con ≤ 3 unidades{outOfStock > 0 ? ` · ${outOfStock} agotados` : ''}
            </p>
          </div>
        </div>
        <Link to="/admin?s=products" className="text-orange-400 hover:text-orange-300 text-xs font-bold uppercase tracking-wider inline-flex items-center gap-1">
          Reabastecer <span aria-hidden>→</span>
        </Link>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
        {items.slice(0, 9).map((p) => {
          const zero = (p.stock || 0) === 0;
          const critical = (p.stock || 0) <= 2;
          const badgeCls = zero
            ? 'bg-rose-500/15 text-rose-300 border-rose-500/30'
            : critical
              ? 'bg-amber-500/15 text-amber-300 border-amber-500/30'
              : 'bg-white/5 text-white/70 border-white/15';
          const isBusy = busy === p.product_id;
          return (
            <div key={p.product_id} data-testid="low-stock-item" className="flex items-center gap-2.5 rounded-xl border border-white/10 bg-black/30 p-2 hover:border-orange-500/40 transition-colors">
              <div className="w-11 h-11 rounded-lg overflow-hidden bg-neutral-900 shrink-0">
                {p.images?.[0] ? (
                  <img src={p.images[0]} alt={p.title} className="w-full h-full object-cover" loading="lazy" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-white/30"><PackageX className="w-4 h-4" /></div>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-white font-semibold text-sm truncate">{p.title}</p>
                <p className="text-white/40 text-[11px] truncate">{p.vendor || '—'}</p>
              </div>
              <span className={`shrink-0 text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full border ${badgeCls}`}>
                {zero ? 'Agotado' : `${p.stock} u.`}
              </span>
              <button
                type="button"
                onClick={() => restock(p, step)}
                disabled={isBusy}
                data-testid={`restock-${p.product_id}`}
                title={`Sumar ${step} unidades`}
                className="shrink-0 h-8 px-2.5 rounded-lg bg-orange-500 hover:bg-orange-400 text-black font-black text-[11px] uppercase tracking-wider inline-flex items-center gap-1 disabled:opacity-50 disabled:cursor-wait transition-colors"
              >
                {isBusy ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <><Plus className="w-3.5 h-3.5" /> {step}</>
                )}
              </button>
            </div>
          );
        })}
      </div>

      {items.length > 9 && (
        <p className="text-white/40 text-[11px] text-center mt-3">
          Mostrando 9 de {items.length}. Ir a <Link to="/admin?s=products" className="text-orange-400 hover:underline">Productos</Link> para ver todos.
        </p>
      )}
    </div>
  );
};

const DashboardSection = ({ user }) => {
  const [stats, setStats] = useState(null);
  const [settings, setSettings] = useState({ low_stock_threshold: 5, restock_step: 10 });
  const [orders, setOrders] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadStats = React.useCallback(() => {
    return api.get('/admin/stats').then((s) => setStats(s.data));
  }, []);

  useEffect(() => {
    Promise.all([
      loadStats(),
      api.get('/admin/orders'),
      api.get('/admin/users?limit=5'),
      api.get('/settings'),
    ]).then(([, o, u, sConf]) => {
      setOrders(o.data.slice(0, 5));
      setUsers(u.data.items);
      setSettings({
        low_stock_threshold: Number(sConf.data?.low_stock_threshold) || 5,
        restock_step: Number(sConf.data?.restock_step) || 10,
      });
    }).finally(() => setLoading(false));
  }, [loadStats]);

  if (loading) return <Loader2 className="w-6 h-6 animate-spin text-orange-500" />;

  const fmt = (n) => (n != null ? Number(n).toLocaleString('es-MX') : '—');
  const fmtMoney = (n) => `$${fmt(n)}`;
  const byStatus = stats?.orders_by_status || {};

  return (
    <div>
      <div className="flex items-baseline gap-3">
        <h1 className="text-2xl md:text-3xl font-black text-white">Dashboard</h1>
        <span className="text-white/50 text-sm">Hola, {user.name || user.email || user.phone}</span>
      </div>
      <p className="text-white/50 mt-1 text-sm">Resumen general del sistema.</p>

      {/* KPI row */}
      <div className="mt-6 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        <KpiCard icon={DollarSign} label="Vendido" value={fmtMoney(stats?.total_revenue)} color="text-orange-400 bg-orange-500/10" hint="Excluye cancelados" big />
        <KpiCard icon={ShoppingBag} label="Pedidos" value={fmt(stats?.orders)} color="text-cyan-400 bg-cyan-500/10" />
        <KpiCard icon={Package} label="Productos" value={fmt(stats?.products)} color="text-fuchsia-400 bg-fuchsia-500/10" />
        <KpiCard icon={Boxes} label="Stock total" value={fmt(stats?.total_stock)} color="text-blue-400 bg-blue-500/10" hint="Unidades disponibles" />
        <KpiCard icon={Users} label="Usuarios" value={fmt(stats?.users)} color="text-emerald-400 bg-emerald-500/10" />
      </div>

      {/* Order pipeline */}
      <div className="mt-5">
        <OrderPipeline counts={byStatus} total={stats?.orders || 0} />
      </div>

      {/* Low stock alert */}
      <LowStockPanel
        items={stats?.low_stock_products || []}
        threshold={stats?.low_stock_threshold || settings.low_stock_threshold}
        step={settings.restock_step}
        onChanged={loadStats}
      />

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-neutral-950 border border-white/10 rounded-2xl p-5">
          <h3 className="text-white font-bold mb-4 flex items-center gap-2"><TrendingUp className="w-4 h-4 text-orange-500" /> Últimos pedidos</h3>
          {orders.length === 0 ? (
            <p className="text-white/50 text-sm">Sin pedidos recientes.</p>
          ) : (
            <ul className="divide-y divide-white/5">
              {orders.map((o) => (
                <li key={o.order_id} className="py-2.5 flex items-center justify-between gap-2 text-sm">
                  <div className="min-w-0">
                    <p className="text-white font-semibold truncate">{o.order_id}</p>
                    <p className="text-white/50 text-xs">{new Date(o.created_at).toLocaleDateString('es-MX')} · {o.status}</p>
                  </div>
                  <span className="text-white font-bold">${o.total?.toLocaleString('es-MX')}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="bg-neutral-950 border border-white/10 rounded-2xl p-5">
          <h3 className="text-white font-bold mb-4 flex items-center gap-2"><Users className="w-4 h-4 text-emerald-400" /> Usuarios recientes</h3>
          {users.length === 0 ? (
            <p className="text-white/50 text-sm">Sin usuarios registrados.</p>
          ) : (
            <ul className="divide-y divide-white/5">
              {users.map((u) => (
                <li key={u.user_id} className="py-2.5 flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-orange-500 text-black flex items-center justify-center font-bold shrink-0">
                    {(u.name || u.phone || u.email || 'U').slice(0, 1).toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-white text-sm font-semibold truncate">{u.name || u.email || u.phone || 'Sin nombre'}</p>
                    <p className="text-white/50 text-xs truncate">{u.email || u.phone}</p>
                  </div>
                  {u.is_admin && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-orange-500/15 text-orange-400 border border-orange-500/30">Admin</span>}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default DashboardSection;
